import { useEffect, useState } from "react";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Trash2, Loader2, Eye, Crown, KeyRound, Users, SlidersHorizontal, RotateCcw } from "lucide-react";

const GB_COMPONENTS = [
  { key: "kehadiran", label: "Kehadiran" },
  { key: "partisipasi", label: "Partisipasi Aktif" },
  { key: "kuis", label: "Kuis" },
  { key: "individu", label: "Presentasi Individu" },
  { key: "kelompok", label: "Presentasi Kelompok" },
];

export default function Settings() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", name: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [pwAdmin, setPwAdmin] = useState({ id: "", old_password: "", new_password: "" });
  const [pwEmp, setPwEmp] = useState({ employee_id: "", new_password: "" });
  const [pwAdminSaving, setPwAdminSaving] = useState(false);
  const [pwEmpSaving, setPwEmpSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [savingGroup, setSavingGroup] = useState("");

  const load = () =>
    api.get("/admins").then((r) => setAdmins(r.data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const toggleComponent = (gid, key) => {
    setGroups((prev) => prev.map((g) => {
      if (g.id !== gid) return g;
      const comps = g.components || [];
      return { ...g, components: comps.includes(key) ? comps.filter((c) => c !== key) : [...comps, key] };
    }));
  };

  const saveGroupComponents = async (g) => {
    const comps = (g.components || []).filter((c) => GB_COMPONENTS.some((x) => x.key === c));
    if (comps.length === 0) { toast.error("Pilih minimal satu komponen untuk dinilai"); return; }
    setSavingGroup(g.id);
    try {
      const { data } = await api.put(`/groups/${g.id}/components`, { components: comps });
      const sres = await api.put(`/groups/${g.id}/attendance-start`, { attendance_start: g.attendance_start || null });
      setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...x, components: data.components, attendance_start: sres.data.attendance_start } : x)));
      toast.success(`Pengaturan "${g.name}" disimpan`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSavingGroup("");
    }
  };

  const changeAdminPassword = async (e) => {
    e.preventDefault();
    if (!pwAdmin.id || !pwAdmin.new_password) { toast.error("Pilih admin dan isi password baru"); return; }
    const isSelf = pwAdmin.id === user?.id;
    if (isSelf && !pwAdmin.old_password) { toast.error("Masukkan password lama untuk akun Anda sendiri"); return; }
    setPwAdminSaving(true);
    try {
      await api.put(`/admins/${pwAdmin.id}/password`, {
        new_password: pwAdmin.new_password,
        old_password: isSelf ? pwAdmin.old_password : undefined,
      });
      toast.success("Password admin berhasil diubah");
      setPwAdmin({ id: "", old_password: "", new_password: "" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setPwAdminSaving(false);
    }
  };

  const changeEmpPassword = async (e) => {
    e.preventDefault();
    if (!pwEmp.employee_id || !pwEmp.new_password) { toast.error("Pilih peserta dan isi password baru"); return; }
    setPwEmpSaving(true);
    try {
      await api.put(`/employees/${pwEmp.employee_id}/password`, { new_password: pwEmp.new_password });
      toast.success("Password peserta berhasil diubah");
      setPwEmp({ employee_id: "", new_password: "" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setPwEmpSaving(false);
    }
  };

  const resetAdminDefault = async () => {
    if (!pwAdmin.id) { toast.error("Pilih admin yang akan direset"); return; }
    const isSelf = pwAdmin.id === user?.id;
    if (isSelf && !pwAdmin.old_password) { toast.error("Masukkan password lama untuk mereset akun Anda sendiri"); return; }
    if (!window.confirm("Reset password admin ini ke default \"admin123\"?")) return;
    setPwAdminSaving(true);
    try {
      await api.put(`/admins/${pwAdmin.id}/password`, {
        new_password: "admin123",
        old_password: isSelf ? pwAdmin.old_password : undefined,
      });
      toast.success("Password admin direset ke default (admin123)");
      setPwAdmin({ id: "", old_password: "", new_password: "" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setPwAdminSaving(false);
    }
  };

  const resetEmpDefault = async () => {
    if (!pwEmp.employee_id) { toast.error("Pilih peserta yang akan direset"); return; }
    if (!window.confirm("Reset password peserta ini ke default \"mankeu123\"?")) return;
    setPwEmpSaving(true);
    try {
      await api.put(`/employees/${pwEmp.employee_id}/password`, { new_password: "mankeu123" });
      toast.success("Password peserta direset ke default (mankeu123)");
      setPwEmp({ employee_id: "", new_password: "" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setPwEmpSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.name.trim() || !form.password) {
      toast.error("Nomor ID, Nama, dan Password wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admins", {
        username: form.username.trim(),
        name: form.name.trim(),
        password: form.password,
      });
      toast.success("Akun admin (hanya lihat) berhasil dibuat");
      setForm({ username: "", name: "", password: "" });
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Hapus akun admin "${a.name}" (${a.username})?`)) return;
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success("Akun admin dihapus");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Pengaturan</h1>
        <p className="mt-1 text-slate-500">Kelola akun administrator. Admin baru bersifat <span className="font-semibold text-slate-700">hanya lihat</span> (tidak dapat mengubah data).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm h-fit">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><UserPlus className="h-5 w-5" /></div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Tambah Admin</h3>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="add-admin-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nomor ID (Username)</label>
              <input
                data-testid="admin-username-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="mis. 5120034"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nama</label>
              <input
                data-testid="admin-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nama lengkap admin"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Kata sandi untuk login"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <button
              data-testid="submit-admin-button"
              type="submit"
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Tambah Admin
            </button>
          </form>
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
            <Eye className="h-4 w-4 mt-0.5 shrink-0" />
            Admin yang ditambahkan hanya dapat <b>melihat</b> data. Semua aksi ubah/tambah/hapus/penilaian/impor dinonaktifkan untuk mereka.
          </div>
        </div>

        <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-[#F16522]"><ShieldCheck className="h-5 w-5" /></div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Daftar Admin</h3>
          </div>
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : (
            <div className="space-y-3" data-testid="admin-list">
              {admins.map((a) => (
                <div key={a.id} data-testid={`admin-row-${a.username}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full ${a.is_super ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                    {a.is_super ? <Crown className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p>
                    <p className="text-xs text-slate-400">ID: {a.username}</p>
                  </div>
                  {a.is_super ? (
                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">Admin Utama</span>
                  ) : (
                    <>
                      <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600">Hanya Lihat</span>
                      <button
                        data-testid={`delete-admin-${a.username}`}
                        onClick={() => remove(a)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                        title="Hapus admin"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><KeyRound className="h-5 w-5" /></div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Ubah Password Admin</h3>
          </div>
          <form onSubmit={changeAdminPassword} className="space-y-4" data-testid="change-admin-password-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pilih Admin</label>
              <select
                data-testid="pw-admin-select"
                value={pwAdmin.id}
                onChange={(e) => setPwAdmin({ ...pwAdmin, id: e.target.value })}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              >
                <option value="">— pilih admin —</option>
                {admins.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.username}){a.id === user?.id ? " — Anda" : ""}</option>
                ))}
              </select>
            </div>
            {pwAdmin.id === user?.id && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password Lama</label>
                <input
                  data-testid="pw-admin-old-input"
                  type="password"
                  value={pwAdmin.old_password}
                  onChange={(e) => setPwAdmin({ ...pwAdmin, old_password: e.target.value })}
                  placeholder="Wajib untuk akun Anda sendiri"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password Baru</label>
              <input
                data-testid="pw-admin-new-input"
                type="password"
                value={pwAdmin.new_password}
                onChange={(e) => setPwAdmin({ ...pwAdmin, new_password: e.target.value })}
                placeholder="Password baru"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <button data-testid="submit-admin-password" type="submit" disabled={pwAdminSaving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition">
              {pwAdminSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Ubah Password Admin
            </button>
            <button data-testid="reset-admin-password" type="button" onClick={resetAdminDefault} disabled={pwAdminSaving} className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60 transition">
              <RotateCcw className="h-4 w-4" /> Reset ke Default (admin123)
            </button>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-[#F16522]"><Users className="h-5 w-5" /></div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Ubah Password Peserta</h3>
          </div>
          <form onSubmit={changeEmpPassword} className="space-y-4" data-testid="change-emp-password-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pilih Peserta</label>
              <select
                data-testid="pw-emp-select"
                value={pwEmp.employee_id}
                onChange={(e) => setPwEmp({ ...pwEmp, employee_id: e.target.value })}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              >
                <option value="">— pilih peserta —</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.employee_id}>{e.name} ({e.employee_id})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password Baru</label>
              <input
                data-testid="pw-emp-new-input"
                type="password"
                value={pwEmp.new_password}
                onChange={(e) => setPwEmp({ ...pwEmp, new_password: e.target.value })}
                placeholder="Password baru"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <button data-testid="submit-emp-password" type="submit" disabled={pwEmpSaving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F16522] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition">
              {pwEmpSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Ubah Password Peserta
            </button>
            <button data-testid="reset-emp-password" type="button" onClick={resetEmpDefault} disabled={pwEmpSaving} className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60 transition">
              <RotateCcw className="h-4 w-4" /> Reset ke Default (mankeu123)
            </button>
          </form>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="group-grading-card">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><SlidersHorizontal className="h-5 w-5" /></div>
          <h3 className="font-heading text-lg font-bold text-slate-900">Penilaian per Kelompok</h3>
        </div>
        <p className="mb-5 text-sm text-slate-500">Tentukan komponen apa saja yang dinilai untuk tiap kelompok. Bobot komponen aktif otomatis dinormalisasi ke 100% saat menghitung nilai akhir. Kelompok "Belum Dikelompokkan" selalu dinilai Kehadiran + Kuis.</p>
        <div className="space-y-4">
          {groups.length === 0 && <p className="text-sm text-slate-400 italic">Belum ada kelompok.</p>}
          {groups.map((g) => (
            <div key={g.id} data-testid={`group-grading-${g.id}`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-semibold text-slate-800">{g.name}</p>
                <button
                  data-testid={`save-group-components-${g.id}`}
                  onClick={() => saveGroupComponents(g)}
                  disabled={savingGroup === g.id}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {savingGroup === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Simpan
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {GB_COMPONENTS.map((c) => {
                  const checked = (g.components || []).includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`group-comp-${g.id}-${c.key}`}
                      onClick={() => toggleComponent(g.id, c.key)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${checked ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-400 hover:border-slate-300"}`}
                    >
                      <span className={`flex h-4 w-4 items-center justify-center rounded ${checked ? "bg-blue-600 text-white" : "bg-slate-100 text-transparent"}`}>✓</span>
                      {c.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <label className="text-xs font-semibold text-slate-600">Mulai penilaian presensi:</label>
                <input
                  type="date"
                  data-testid={`group-att-start-${g.id}`}
                  value={g.attendance_start || ""}
                  onChange={(e) => setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...x, attendance_start: e.target.value } : x)))}
                  className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs outline-none focus:border-blue-500"
                />
                <span className="text-[11px] text-slate-400">Presensi sebelum tanggal ini tidak dihitung; perbandingan nilai per kelompok sendiri.</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
